// ─── Booking Page Theme System ───────────────────────────────────────────────
// Each theme is a set of CSS custom properties injected on the root div
// of PublicBookingPage. The Sheet (booking flow) is NOT themed.

export type BookingThemeId =
  | 'premium-minimal'
  | 'clinical-clean'
  | 'wellness-organic'
  | 'bold-modern';

export interface BookingTheme {
  id: BookingThemeId;
  name: string;
  tagline: string;
  idealFor: string;
  accentColor: string;   // for thumbnail rendering
  bgColor: string;       // solid bg for thumbnail (no gradient)
  heroColor: string;     // hero strip color for thumbnail
  vars: Record<string, string>;
}

export const BOOKING_THEMES: BookingTheme[] = [
  // ── 1. PREMIUM MINIMAL ────────────────────────────────────────────────────
  {
    id: 'premium-minimal',
    name: 'Premium Minimal',
    tagline: 'Editorial · B&W · Elegante',
    idealFor: 'Nail art, salones premium, estética de autor',
    accentColor: '#111111',
    bgColor: '#ffffff',
    heroColor: '#111111',
    vars: {
      '--pb-font-body':            "'DM Sans', sans-serif",
      '--pb-font-heading':         "'Playfair Display', serif",
      '--pb-bg':                   '#ffffff',
      '--pb-text':                 '#111111',
      '--pb-text-muted':           '#888888',
      '--pb-text-secondary':       '#555555',
      '--pb-border':               '#f0f0f0',
      '--pb-hero-overlay':         'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.72) 100%)',
      '--pb-hero-bg-fallback':     'linear-gradient(160deg, #222 0%, #111 100%)',
      '--pb-hero-img-filter':      'grayscale(100%)',
      '--pb-hero-img-opacity':     '0.6',
      '--pb-hero-btn-bg':          'white',
      '--pb-hero-btn-color':       '#111',
      '--pb-hero-btn-radius':      '0px',
      '--pb-infobar-bg':           'transparent',
      '--pb-dot-color':            '#111',
      '--pb-phone-color':          '#111',
      '--pb-pro-photo-border':     '#e8e8e8',
      '--pb-pro-initials-bg':      '#f5f5f5',
      '--pb-pro-initials-color':   '#888',
      '--pb-tab-active-bg':        '#111',
      '--pb-tab-active-color':     '#fff',
      '--pb-tab-active-border':    '#111',
      '--pb-tab-inactive-bg':      'white',
      '--pb-tab-inactive-color':   '#555',
      '--pb-tab-inactive-border':  '#ccc',
      '--pb-tab-radius':           '0px',
      '--pb-svc-border':           '#f0f0f0',
      '--pb-svc-name-color':       '#111',
      '--pb-svc-meta-color':       '#888',
      '--pb-svc-btn-sel-bg':       '#111',
      '--pb-svc-btn-sel-border':   '#111',
      '--pb-svc-btn-sel-color':    'white',
      '--pb-svc-btn-bg':           'white',
      '--pb-svc-btn-border':       '#ccc',
      '--pb-svc-btn-color':        '#555',
      '--pb-svc-btn-radius':       '50%',
      '--pb-gallery-filter':       'grayscale(80%)',
      '--pb-gallery-filter-hover': 'grayscale(0%)',
      '--pb-sticky-bg':            '#111',
      '--pb-sticky-count-color':   'rgba(255,255,255,0.55)',
      '--pb-sticky-price-color':   '#fff',
      '--pb-sticky-btn-bg':        '#fff',
      '--pb-sticky-btn-color':     '#111',
      '--pb-sticky-btn-radius':    '0px',
      '--pb-modal-bg':             '#ffffff',
      '--pb-modal-text':           '#111111',
      '--pb-modal-muted':          '#888888',
      '--pb-modal-accent':         '#111111',
      '--pb-modal-border':         '#ebebeb',
      '--pb-modal-card':           '#f7f7f7',
      '--pb-modal-handle':         '#dddddd',
    },
  },

  // ── 2. CLINICAL CLEAN ─────────────────────────────────────────────────────
  {
    id: 'clinical-clean',
    name: 'Clinical Clean',
    tagline: 'Limpio · Confiable · Estructurado',
    idealFor: 'Medicina estética, dermatología, nutrición, psicología',
    accentColor: '#4b90b9',
    bgColor: '#edf6fb',
    heroColor: '#1A2B3C',
    vars: {
      '--pb-font-body':            'Inter, system-ui, sans-serif',
      '--pb-font-heading':         'Inter, system-ui, sans-serif',
      '--pb-bg':                   'linear-gradient(180deg, #edf6fb 0%, #e2eff8 100%)',
      '--pb-text':                 '#1A2B3C',
      '--pb-text-muted':           '#607688',
      '--pb-text-secondary':       '#607688',
      '--pb-border':               'rgba(46,89,114,0.12)',
      '--pb-hero-overlay':         'linear-gradient(to bottom, rgba(20,50,75,0.08) 0%, rgba(20,50,75,0.70) 100%)',
      '--pb-hero-bg-fallback':     'linear-gradient(135deg, #dfeef8 0%, #bcd8ea 100%)',
      '--pb-hero-img-filter':      'grayscale(0%)',
      '--pb-hero-img-opacity':     '0.75',
      '--pb-hero-btn-bg':          '#4b90b9',
      '--pb-hero-btn-color':       '#fff',
      '--pb-hero-btn-radius':      '8px',
      '--pb-infobar-bg':           'rgba(255,255,255,0.85)',
      '--pb-dot-color':            '#4b90b9',
      '--pb-phone-color':          '#4b90b9',
      '--pb-pro-photo-border':     'rgba(46,89,114,0.15)',
      '--pb-pro-initials-bg':      '#d4e6f2',
      '--pb-pro-initials-color':   '#4b90b9',
      '--pb-tab-active-bg':        '#4b90b9',
      '--pb-tab-active-color':     '#fff',
      '--pb-tab-active-border':    '#4b90b9',
      '--pb-tab-inactive-bg':      'rgba(255,255,255,0.6)',
      '--pb-tab-inactive-color':   '#607688',
      '--pb-tab-inactive-border':  'rgba(46,89,114,0.2)',
      '--pb-tab-radius':           '8px',
      '--pb-svc-border':           'rgba(46,89,114,0.1)',
      '--pb-svc-name-color':       '#1A2B3C',
      '--pb-svc-meta-color':       '#607688',
      '--pb-svc-btn-sel-bg':       '#4b90b9',
      '--pb-svc-btn-sel-border':   '#4b90b9',
      '--pb-svc-btn-sel-color':    'white',
      '--pb-svc-btn-bg':           'rgba(255,255,255,0.6)',
      '--pb-svc-btn-border':       'rgba(46,89,114,0.25)',
      '--pb-svc-btn-color':        '#607688',
      '--pb-svc-btn-radius':       '8px',
      '--pb-gallery-filter':       'grayscale(0%)',
      '--pb-gallery-filter-hover': 'grayscale(0%) brightness(1.05)',
      '--pb-sticky-bg':            '#4b90b9',
      '--pb-sticky-count-color':   'rgba(255,255,255,0.75)',
      '--pb-sticky-price-color':   '#fff',
      '--pb-sticky-btn-bg':        '#fff',
      '--pb-sticky-btn-color':     '#4b90b9',
      '--pb-sticky-btn-radius':    '8px',
      '--pb-modal-bg':             '#ffffff',
      '--pb-modal-text':           '#1A2B3C',
      '--pb-modal-muted':          '#607688',
      '--pb-modal-accent':         '#4b90b9',
      '--pb-modal-border':         'rgba(46,89,114,0.15)',
      '--pb-modal-card':           '#f0f6fa',
      '--pb-modal-handle':         'rgba(46,89,114,0.2)',
    },
  },

  // ── 3. WELLNESS ORGANIC ───────────────────────────────────────────────────
  {
    id: 'wellness-organic',
    name: 'Wellness Organic',
    tagline: 'Cálido · Sensorial · Relajante',
    idealFor: 'Spa, masajes, yoga, pilates, belleza natural',
    accentColor: '#9BAF8E',
    bgColor: '#FAF7F2',
    heroColor: '#3E2C1E',
    vars: {
      '--pb-font-body':            "'Lato', 'DM Sans', sans-serif",
      '--pb-font-heading':         "'Cormorant Garamond', 'Playfair Display', serif",
      '--pb-bg':                   '#FAF7F2',
      '--pb-text':                 '#2C1F14',
      '--pb-text-muted':           '#9B8A78',
      '--pb-text-secondary':       '#7A6856',
      '--pb-border':               '#EDE5DB',
      '--pb-hero-overlay':         'linear-gradient(to top, rgba(62,44,30,0.88) 0%, rgba(62,44,30,0.25) 58%, transparent 100%)',
      '--pb-hero-bg-fallback':     'linear-gradient(160deg, #C8B89A 0%, #8B7355 100%)',
      '--pb-hero-img-filter':      'grayscale(0%) sepia(10%)',
      '--pb-hero-img-opacity':     '0.8',
      '--pb-hero-btn-bg':          '#fff',
      '--pb-hero-btn-color':       '#3E2C1E',
      '--pb-hero-btn-radius':      '40px',
      '--pb-infobar-bg':           '#FAF7F2',
      '--pb-dot-color':            '#9BAF8E',
      '--pb-phone-color':          '#7A6856',
      '--pb-pro-photo-border':     '#EDE5DB',
      '--pb-pro-initials-bg':      '#E8DDD0',
      '--pb-pro-initials-color':   '#7A6856',
      '--pb-tab-active-bg':        '#EDE5DB',
      '--pb-tab-active-color':     '#2C1F14',
      '--pb-tab-active-border':    '#C8B89A',
      '--pb-tab-inactive-bg':      'transparent',
      '--pb-tab-inactive-color':   '#7A6856',
      '--pb-tab-inactive-border':  '#EDE5DB',
      '--pb-tab-radius':           '12px',
      '--pb-svc-border':           '#EDE5DB',
      '--pb-svc-name-color':       '#2C1F14',
      '--pb-svc-meta-color':       '#9B8A78',
      '--pb-svc-btn-sel-bg':       '#9BAF8E',
      '--pb-svc-btn-sel-border':   '#9BAF8E',
      '--pb-svc-btn-sel-color':    'white',
      '--pb-svc-btn-bg':           'transparent',
      '--pb-svc-btn-border':       '#C8B89A',
      '--pb-svc-btn-color':        '#7A6856',
      '--pb-svc-btn-radius':       '50%',
      '--pb-gallery-filter':       'grayscale(0%) sepia(15%)',
      '--pb-gallery-filter-hover': 'grayscale(0%) sepia(0%)',
      '--pb-sticky-bg':            '#3E2C1E',
      '--pb-sticky-count-color':   'rgba(255,255,255,0.55)',
      '--pb-sticky-price-color':   '#fff',
      '--pb-sticky-btn-bg':        '#FAF7F2',
      '--pb-sticky-btn-color':     '#3E2C1E',
      '--pb-sticky-btn-radius':    '40px',
      '--pb-modal-bg':             '#FAF7F2',
      '--pb-modal-text':           '#2C1F14',
      '--pb-modal-muted':          '#9B8A78',
      '--pb-modal-accent':         '#9BAF8E',
      '--pb-modal-border':         '#EDE5DB',
      '--pb-modal-card':           '#F0EAE0',
      '--pb-modal-handle':         '#D8CFBF',
    },
  },

  // ── 4. BOLD MODERN ────────────────────────────────────────────────────────
  {
    id: 'bold-modern',
    name: 'Bold Modern',
    tagline: 'Fuerte · Dinámico · Con marca',
    idealFor: 'Barberías, salones urbanos, cejas, estudios digitales',
    accentColor: '#FF2D55',
    bgColor: '#0A0A0A',
    heroColor: '#111111',
    vars: {
      '--pb-font-body':            "'Space Grotesk', 'Inter', sans-serif",
      '--pb-font-heading':         "'Space Grotesk', 'Inter', sans-serif",
      '--pb-bg':                   '#0A0A0A',
      '--pb-text':                 '#ffffff',
      '--pb-text-muted':           '#666666',
      '--pb-text-secondary':       '#999999',
      '--pb-border':               '#1E1E1E',
      '--pb-hero-overlay':         'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.82) 100%)',
      '--pb-hero-bg-fallback':     'linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)',
      '--pb-hero-img-filter':      'grayscale(30%)',
      '--pb-hero-img-opacity':     '0.45',
      '--pb-hero-btn-bg':          '#FF2D55',
      '--pb-hero-btn-color':       '#fff',
      '--pb-hero-btn-radius':      '4px',
      '--pb-infobar-bg':           '#FF2D55',
      '--pb-dot-color':            '#fff',
      '--pb-phone-color':          'rgba(255,255,255,0.85)',
      '--pb-pro-photo-border':     '#2A2A2A',
      '--pb-pro-initials-bg':      '#1E1E1E',
      '--pb-pro-initials-color':   '#555',
      '--pb-tab-active-bg':        'rgba(255,45,85,0.12)',
      '--pb-tab-active-color':     '#FF2D55',
      '--pb-tab-active-border':    '#FF2D55',
      '--pb-tab-inactive-bg':      'transparent',
      '--pb-tab-inactive-color':   '#555',
      '--pb-tab-inactive-border':  '#2A2A2A',
      '--pb-tab-radius':           '4px',
      '--pb-svc-border':           '#1A1A1A',
      '--pb-svc-name-color':       '#ffffff',
      '--pb-svc-meta-color':       '#555555',
      '--pb-svc-btn-sel-bg':       '#FF2D55',
      '--pb-svc-btn-sel-border':   '#FF2D55',
      '--pb-svc-btn-sel-color':    '#fff',
      '--pb-svc-btn-bg':           'transparent',
      '--pb-svc-btn-border':       '#333',
      '--pb-svc-btn-color':        '#555',
      '--pb-svc-btn-radius':       '4px',
      '--pb-gallery-filter':       'grayscale(20%)',
      '--pb-gallery-filter-hover': 'grayscale(0%)',
      '--pb-sticky-bg':            '#111',
      '--pb-sticky-count-color':   '#FF2D55',
      '--pb-sticky-price-color':   '#fff',
      '--pb-sticky-btn-bg':        '#FF2D55',
      '--pb-sticky-btn-color':     '#fff',
      '--pb-sticky-btn-radius':    '4px',
      '--pb-modal-bg':             '#111111',
      '--pb-modal-text':           '#ffffff',
      '--pb-modal-muted':          '#888888',
      '--pb-modal-accent':         '#FF2D55',
      '--pb-modal-border':         '#2A2A2A',
      '--pb-modal-card':           '#1A1A1A',
      '--pb-modal-handle':         '#333333',
    },
  },
];

export function getBookingTheme(id: string | undefined): BookingTheme {
  return BOOKING_THEMES.find((t) => t.id === id) ?? BOOKING_THEMES[0];
}
